import React from 'react';
import { motion } from 'framer-motion';
import { FaTooth, FaSmile, FaTeeth, FaStethoscope, FaChild, FaSyringe, FaCrown, FaExclamationTriangle, FaBrush, FaXRay } from 'react-icons/fa';
import PageMeta from './PageMeta';

const services = [
  {
    icon: <FaTooth className="text-phoenix-gold w-8 h-8" />,
    title: 'General Dentistry',
    description: 'Comprehensive dental exams, cleanings, and preventive care to maintain optimal oral health.',
  },
  {
    icon: <FaSmile className="text-phoenix-gold w-8 h-8" />,
    title: 'Cosmetic Dentistry',
    description: 'Teeth whitening, veneers, and smile makeovers to enhance your appearance.',
  },
  {
    icon: <FaTeeth className="text-phoenix-gold w-8 h-8" />,
    title: 'Orthodontics',
    description: 'Braces and Invisalign for straightening teeth and correcting bite issues.',
  },
  {
    icon: <FaStethoscope className="text-phoenix-gold w-8 h-8" />,
    title: 'Oral Surgery',
    description: 'Extractions, implants, and other surgical procedures for complex dental needs.',
  },
  {
    icon: <FaChild className="text-phoenix-gold w-8 h-8" />,
    title: 'Pediatric Dentistry',
    description: 'Specialized dental care for children, including preventive and restorative treatments.',
  },
  {
    icon: <FaSyringe className="text-phoenix-gold w-8 h-8" />,
    title: 'Periodontics',
    description: 'Treatment for gum diseases and maintenance of healthy gums.',
  },
  {
    icon: <FaCrown className="text-phoenix-gold w-8 h-8" />,
    title: 'Endodontics',
    description: 'Root canal therapy and treatment of dental pulp and surrounding tissues.',
  },
  {
    icon: <FaExclamationTriangle className="text-phoenix-gold w-8 h-8" />,
    title: 'Emergency Dentistry',
    description: 'Immediate care for dental emergencies like toothaches, broken teeth, or injuries.',
  },
  {
    icon: <FaBrush className="text-phoenix-gold w-8 h-8" />,
    title: 'Teeth Cleaning',
    description: 'Professional cleaning to remove plaque, tartar, and stains for healthy teeth.',
  },
  {
    icon: <FaXRay className="text-phoenix-gold w-8 h-8" />,
    title: 'Dental X-Rays',
    description: 'Diagnostic imaging to detect hidden dental issues and plan treatments.',
  },
];

const Services = () => {
  return (
    <div className="bg-phoenix-beige min-h-screen">
      <PageMeta
        title="Our Services | Phoenix Dental"
        description="Explore our comprehensive range of dental services including cosmetic dentistry, orthodontics, and emergency care."
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-phoenix-beige via-phoenix-white to-phoenix-beige py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            className="text-5xl lg:text-6xl font-bold text-phoenix-black mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our <span className="text-phoenix-gold">Dental Services</span>
          </motion.h1>
          <motion.p
            className="text-xl text-phoenix-gray max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Comprehensive dental care tailored to your needs. From routine check-ups to advanced treatments,
            we provide exceptional services to keep your smile healthy and bright.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <FaTooth className="text-6xl text-phoenix-gold mx-auto" />
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {services.map(({ icon, title, description }, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-phoenix-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="mb-4">{icon}</div>
                <h3 className="text-xl font-semibold text-phoenix-black mb-2">{title}</h3>
                <p className="text-phoenix-gray">{description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
